import React, {createContext, useContext, useEffect, useReducer, useRef} from "react";
import {isDateInPast} from "../app/dateFormat";

export const UPDATE_PROGRESS = 'dmsContext/updateProgress';
export const UPDATE_URLS = 'dmsContext/updateUrls';
export const REMOVE_FILE_PROGRESS = 'dmsContext/removeFileProgress';
export const SWEEP_EXPIRED_URLS = 'dmsContext/sweepExpiredUrls';

/** How often to scan for and evict expired blob-URL cache entries (ms). */
const SWEEP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const DmsStateContext = createContext(undefined);
const DmsDispatchContext = createContext(undefined);

const initialState = {
    progressByFile: {},
    urls: {}
}

const dmsReducer = (state, action) => {
    switch (action.type) {
        case UPDATE_PROGRESS:
            return {
                ...state,
                progressByFile: {
                    ...state.progressByFile,
                    [action.fileName]: action.progress
                }
            }
        case UPDATE_URLS:
            const previousDimensions = state.urls[action.dmsId] ? state.urls[action.dmsId].dimensions : {}
            return {
                ...state,
                urls: {
                    ...state.urls,
                    [action.dmsId]: {
                        ...state.urls[action.dmsId],
                        url: action.url,
                        dimensions: {
                            ...previousDimensions,
                            ...action.dimensions
                        },
                        expires: action.expires,
                        static: action.static,
                        failed: action.failed
                    }
                }
            }
        case REMOVE_FILE_PROGRESS:
            return {
                ...state,
                urls: Object.keys(state.urls)
                    .filter(dmsId => dmsId !== action.dmsId)
                    .reduce((object, dmsId) => ({
                        ...object,
                        [dmsId]: state.urls[dmsId]
                    }), {}),
                progressByFile: Object.keys(state.progressByFile)
                    .filter(fileName => fileName !== action.title)
                    .reduce((object, fileName) => ({
                        ...object,
                        [fileName]: state.progressByFile[fileName]
                    }), {})
            }
        case SWEEP_EXPIRED_URLS: {
            // Remove all entries whose IDs are listed in action.dmsIds.
            // The caller has already revoked the associated blob URLs before dispatching.
            const remaining = Object.keys(state.urls)
                .filter(dmsId => !action.dmsIds.includes(dmsId))
                .reduce((obj, dmsId) => ({...obj, [dmsId]: state.urls[dmsId]}), {});
            return {...state, urls: remaining};
        }
        default:
            throw new Error(`Unhandled action type: ${action.type}`)
    }
}

export const DmsProvider = ({children}) => {
    const [state, dispatch] = useReducer(dmsReducer, initialState, undefined);

    // Keep a ref so the sweep interval always sees the latest state without
    // being added to the interval's dependency array (which would restart it).
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        const sweepInterval = setInterval(() => {
            const currentUrls = stateRef.current.urls;
            const expiredEntries = Object.entries(currentUrls)
                .filter(([, entry]) => entry.expires && isDateInPast(entry.expires));

            if (expiredEntries.length === 0) {
                return;
            }

            // Revoke blob URLs on the main thread before removing the cache entries.
            for (const [, entry] of expiredEntries) {
                if (entry.dimensions) {
                    for (const blobUrl of Object.values(entry.dimensions)) {
                        if (typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
                            URL.revokeObjectURL(blobUrl);
                        }
                    }
                }
            }

            dispatch({
                type: SWEEP_EXPIRED_URLS,
                dmsIds: expiredEntries.map(([dmsId]) => dmsId),
            });
        }, SWEEP_INTERVAL_MS);

        return () => clearInterval(sweepInterval);
    }, []);

    return (
        <DmsStateContext.Provider value={state}>
            <DmsDispatchContext.Provider value={dispatch}>
                {children}
            </DmsDispatchContext.Provider>
        </DmsStateContext.Provider>
    )
}

export const useDmsState = () => {
    const context = useContext(DmsStateContext);
    if (undefined === context) {
        throw new Error('useDmsState must be used within a DmsProvider');
    } else {
        return context;
    }
}

export const useDmsDispatch = () => {
    const context = useContext(DmsDispatchContext);
    if (undefined === context) {
        throw new Error('useDmsDispatch must be used withing a DmsProvider')
    } else {
        return context;
    }
}
